import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { lazy } from 'react';

const SuperAdminCampaignForm = lazy(() => import('@/components/SuperAdminCampaignForm'));
const ClientCampaignForm = lazy(() => import('@/components/ClientCampaignFormTemp'));
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { useAuth } from '@/contexts/AuthContext';
import {
  Send,
  Plus,
  FileText,
  MessageSquare,
  Calendar,
  Play,
  Pause,
  X,
  Search,
  BarChart,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import api from '@/lib/api';

interface Campaign {
  id: number;
  name: string;
  description?: string;
  campaign_type: 'integrada' | 'avulsa';
  status: 'rascunho' | 'agendada' | 'em_andamento' | 'pausada' | 'concluida' | 'cancelada';
  integration_type: 'hinova' | 'sem_integracao';
  message_content?: string;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  whatsappConnection?: {
    id: number;
    name: string;
    connection_type: string;
  };
  client?: {
    id: number;
    legal_name: string;
  };
  daily_limit?: number;
}

interface WhatsAppConnection {
  id: number;
  name: string;
  connection_type: string;
  is_active: boolean;
}

const Campaigns = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [connections, setConnections] = useState<WhatsAppConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuperAdminForm, setShowSuperAdminForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [campaignContacts, setCampaignContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  


  useEffect(() => {
    loadCampaigns();
    loadConnections();
  }, []);

  const loadCampaigns = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/campaigns', {
        params: { status: statusFilter !== 'all' ? statusFilter : undefined }
      });
      setCampaigns(response.campaigns || []);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar campanhas",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadConnections = async () => {
    try {
      const response = await api.get('/whatsapp/client/current');
      setConnections(response.connections?.filter((conn: WhatsAppConnection) => conn.is_active) || []);
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    }
  };

  const loadCampaignContacts = async (campaignId: number) => {
    try {
      setContactsLoading(true);
      const response = await api.get(`/campaigns/${campaignId}/contacts`);
      setCampaignContacts(response.contacts || []);
    } catch (error) {
      console.error('Erro ao carregar contatos da campanha:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar contatos da campanha",
        variant: "destructive"
      });
    } finally {
      setContactsLoading(false);
    }
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    if (user?.role === 'admin') {
      setShowSuperAdminForm(true);
    } else {
      setShowClientForm(true);
    }
  };

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailsModal(true);
    loadCampaignContacts(campaign.id);
  };



  const handleStartCampaign = async (campaignId: number) => {
    try {
      await api.post(`/campaigns/${campaignId}/start`);
      toast({
        title: "Sucesso",
        description: "Campanha iniciada com sucesso"
      });
      loadCampaigns();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao iniciar campanha",
        variant: "destructive"
      });
    }
  };

  const handlePauseCampaign = async (campaignId: number) => {
    try {
      await api.post(`/campaigns/${campaignId}/pause`);
      toast({
        title: "Sucesso",
        description: "Campanha pausada"
      });
      loadCampaigns();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao pausar campanha",
        variant: "destructive"
      });
    }
  };

  const handleCancelCampaign = async (campaignId: number) => {
    try {
      await api.post(`/campaigns/${campaignId}/cancel`);
      toast({
        title: "Sucesso",
        description: "Campanha cancelada"
      });
      loadCampaigns();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao cancelar campanha",
        variant: "destructive"
      });
    }
  };



  const getStatusBadge = (status: string) => {
    const statusConfig = {
      rascunho: { label: 'Rascunho', variant: 'outline' as const, icon: FileText },
      agendada: { label: 'Agendada', variant: 'secondary' as const, icon: Calendar },
      em_andamento: { label: 'Em Andamento', variant: 'default' as const, icon: Play },
      pausada: { label: 'Pausada', variant: 'secondary' as const, icon: Pause },
      concluida: { label: 'Concluída', variant: 'outline' as const, icon: CheckCircle },
      cancelada: { label: 'Cancelada', variant: 'destructive' as const, icon: XCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <Layout>
        <AtomicLoadingScreen />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Campanhas</h2>
            <p className="text-muted-foreground">
              Gerencie suas campanhas de mensagens em massa
            </p>
          </div>
          
          {user?.role === 'admin' ? (
            <Button 
              onClick={() => setShowSuperAdminForm(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nova Campanha (SuperAdmin)
            </Button>
          ) : (
            <Button 
              onClick={() => setShowClientForm(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nova Campanha
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar campanhas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="rascunho">Rascunho</SelectItem>
              <SelectItem value="agendada">Agendada</SelectItem>
              <SelectItem value="em_andamento">Em Andamento</SelectItem>
              <SelectItem value="pausada">Pausada</SelectItem>
              <SelectItem value="concluida">Concluída</SelectItem>
              <SelectItem value="cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Campanhas</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{campaigns.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
              <Play className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {campaigns.filter(c => c.status === 'em_andamento').length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mensagens Enviadas</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {campaigns.reduce((acc, c) => acc + c.sent_count, 0)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
              <BarChart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {campaigns.length > 0 
                  ? `${Math.round(
                      (campaigns.reduce((acc, c) => acc + c.sent_count, 0) / 
                       campaigns.reduce((acc, c) => acc + c.total_contacts || 1, 0)) * 100
                    )}%`
                  : '0%'
                }
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaigns Table */}
        <Card>
          <CardHeader>
            <CardTitle>Campanhas</CardTitle>
            <CardDescription>
              Visualize e gerencie suas campanhas de mensagens
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Conexão</TableHead>
                  <TableHead className="text-center">Contatos</TableHead>
                  <TableHead className="text-center">Enviadas</TableHead>
                  <TableHead className="text-center">Falhas</TableHead>
                  <TableHead>Progresso e Controles</TableHead>
                  <TableHead>Agendada para</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="font-medium">{campaign.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {campaign.campaign_type === 'integrada' ? 'Integrada' : 'Avulsa'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                    <TableCell>{campaign.whatsappConnection?.name}</TableCell>
                    <TableCell className="text-center">{campaign.total_contacts}</TableCell>
                    <TableCell className="text-center">{campaign.sent_count}</TableCell>
                    <TableCell className="text-center">{campaign.failed_count}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        {/* Barra de Progresso */}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <div className="text-sm font-medium">
                              {campaign.total_contacts > 0 
                                ? Math.round((campaign.sent_count / campaign.total_contacts) * 100)
                                : 0}%
                            </div>
                            <div className="text-xs text-gray-500">
                              ({campaign.sent_count}/{campaign.total_contacts})
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${campaign.total_contacts > 0 
                                  ? Math.round((campaign.sent_count / campaign.total_contacts) * 100)
                                  : 0}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                        
                        {/* Botões de Controle */}
                        <div className="flex items-center space-x-1">
                          {campaign.status === 'em_andamento' && (
                            <Button 
                              variant="secondary" 
                              size="sm" 
                              onClick={() => handlePauseCampaign(campaign.id)}
                              className="h-8 px-2"
                            >
                              <Pause className="h-3 w-3" />
                            </Button>
                          )}
                          {campaign.status === 'pausada' && (
                            <Button 
                              variant="default" 
                              size="sm" 
                              onClick={() => handleStartCampaign(campaign.id)}
                              className="h-8 px-2"
                            >
                              <Play className="h-3 w-3" />
                            </Button>
                          )}
                          {(campaign.status === 'em_andamento' || campaign.status === 'pausada') && (
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={() => handleCancelCampaign(campaign.id)}
                              className="h-8 px-2"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {campaign.scheduled_at 
                        ? new Date(campaign.scheduled_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      {new Date(campaign.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => handleViewDetails(campaign)}>
                        Ver Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Campanha</DialogTitle>
            <DialogDescription>
              {selectedCampaign?.name} - Visualize todos os contatos e status de envio
            </DialogDescription>
          </DialogHeader>

          {selectedCampaign && (
            <div className="space-y-6">
              {/* Campaign Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Informações da Campanha</h3>
                  <div className="mt-2 space-y-1">
                    <p><span className="font-medium">Nome:</span> {selectedCampaign.name}</p>
                    <p><span className="font-medium">Tipo:</span> {selectedCampaign.campaign_type === 'integrada' ? 'Integrada' : 'Avulsa'}</p>
                    <p><span className="font-medium">Status:</span> {getStatusBadge(selectedCampaign.status)}</p>
                    <p><span className="font-medium">Conexão:</span> {selectedCampaign.whatsappConnection?.name}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Estatísticas</h3>
                  <div className="mt-2 space-y-1">
                    <p><span className="font-medium">Total de Contatos:</span> {selectedCampaign.total_contacts}</p>
                    <p><span className="font-medium">Mensagens Enviadas:</span> {selectedCampaign.sent_count}</p>
                    <p><span className="font-medium">Falhas:</span> {selectedCampaign.failed_count}</p>
                    {selectedCampaign.scheduled_at && (
                      <p><span className="font-medium">Agendada para:</span> {new Date(selectedCampaign.scheduled_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-sm text-gray-600 mb-2">Mensagem</h3>
                <p className="text-sm bg-white p-3 rounded border">{selectedCampaign.message_content || 'Mensagem não disponível'}</p>
              </div>

              {/* Search Contacts */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar contatos..."
                    value={contactSearchTerm}
                    onChange={(e) => setContactSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Badge variant="outline" className="px-3 py-1">
                  {campaignContacts.filter(contact => 
                    contact.phone_number.includes(contactSearchTerm) ||
                    (contact.name && contact.name.toLowerCase().includes(contactSearchTerm.toLowerCase()))
                  ).length} contatos
                </Badge>
              </div>

              {/* Contacts Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contato</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Data/Hora do Envio</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Mensagem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contactsLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="ml-2">Carregando contatos...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      campaignContacts
                        .filter(contact => 
                          contact.phone_number.includes(contactSearchTerm) ||
                          (contact.name && contact.name.toLowerCase().includes(contactSearchTerm.toLowerCase()))
                        )
                        .map((contact) => (
                          <TableRow key={contact.id}>
                            <TableCell className="font-medium">{contact.phone_number}</TableCell>
                            <TableCell>{contact.name || '-'}</TableCell>
                            <TableCell>
                              {contact.sent_at 
                                ? new Date(contact.sent_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
                                : '-'
                              }
                            </TableCell>
                            <TableCell>
                              {contact.sent_at ? (
                                contact.failed_at ? (
                                  <Badge variant="destructive" className="flex items-center gap-1">
                                    <XCircle className="h-3 w-3" />
                                    Falha
                                  </Badge>
                                ) : (
                                  <Badge variant="default" className="flex items-center gap-1 bg-green-500 hover:bg-green-600">
                                    <CheckCircle className="h-3 w-3" />
                                    Enviado
                                  </Badge>
                                )
                              ) : (
                                <Badge variant="outline" className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Pendente
                                </Badge>
                              )}
                            </TableCell>
                                                         <TableCell className="max-w-xs">
                               <div className="truncate text-sm text-gray-600">
                                 {selectedCampaign.message_content || 'Mensagem não disponível'}
                               </div>
                             </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Forms - Carregamento Condicional */}
      {user?.role === 'admin' && (
        <Suspense fallback={<div>Carregando formulário de admin...</div>}>
          <SuperAdminCampaignForm
            open={showSuperAdminForm}
            onClose={() => setShowSuperAdminForm(false)}
            onSuccess={loadCampaigns}
          />
        </Suspense>
      )}

      {user?.role !== 'admin' && (
        <Suspense fallback={<div>Carregando formulário de cliente...</div>}>
          <ClientCampaignForm
            open={showClientForm}
            onClose={() => setShowClientForm(false)}
            onSuccess={loadCampaigns}
            editingCampaign={editingCampaign}
          />
        </Suspense>
      )}
      </Layout>
    );
  };

export default Campaigns; 